import type {
    BillingDetails,
    CreateContractSyncSDK,
} from '../../checkout-be/src/schemas/createContractSdk'
import type { AchFormData, Contract } from '../models/sdkModels'
import type { CheckoutHandler } from './sdk'

export const setBillingDetails = (
    checkoutInstance: CheckoutHandler,
    achFormData?: AchFormData | null,
    pspToken?: string,
): BillingDetails => {
    let billingDetails: BillingDetails = {} as BillingDetails

    switch (checkoutInstance.paymentOption) {
        case 'ach':
            billingDetails = {
                type: checkoutInstance.paymentOption,
                accountNo: achFormData?.accountNumber as string,
                accountType: 'CHECKING',
                routingNumber: achFormData?.routingNumber as string,
                accountHolderName: `${checkoutInstance.customer.firstName} ${checkoutInstance.customer.lastName}`,
            }
            break

        case 'card':
            billingDetails = {
                type: checkoutInstance.paymentOption,
                pspToken: pspToken as string,
            }
            break
    }

    return billingDetails
}

export const createContractPayload = (
    checkoutInstance: CheckoutHandler,
    achFormData?: AchFormData | null,
    pspToken?: string,
): CreateContractSyncSDK => {
    // TODO: Tech debt, bind this to the checkout instance on initialisation
    const achDiscountCodeApplied = checkoutInstance.basket.offers.every(
        (offer) =>
            offer.contracts.every(
                (contract) =>
                    contract.contractAttributes.ach?.discountProfileAttributes
                        ?.achDiscountCodeApplied,
            ),
    )
    return {
        transactionId: checkoutInstance.transactionId,
        channelSourceData: {
            checkoutSourceRef:
                checkoutInstance.basket.channelSourceData.checkoutSourceRef,
        },
        person: {
            personId: checkoutInstance.customer.personId,
        },
        billing: {
            address: {
                city: checkoutInstance.customer.address.city,
                countryCode: checkoutInstance.customer.address.countryCode,
                postalCode: checkoutInstance.customer.address.postalCode,
                stateCode: checkoutInstance.customer.address.stateCode,
                street: checkoutInstance.customer.address.stateCode,
                subBuilding: checkoutInstance.customer.address.subBuilding,
            },
            billingDetails: setBillingDetails(
                checkoutInstance,
                achFormData,
                pspToken,
            ),
        },
        achDiscountCode:
            checkoutInstance.paymentOption === 'ach' && achDiscountCodeApplied
                ? checkoutInstance.basket.offers[0].contracts[0]
                      .contractAttributes.ach?.discountProfileAttributes
                      ?.achDiscountCodeApplied
                : undefined,
        offers: createOffersToSendToBackend(checkoutInstance),
    }
}

export function createOffersToSendToBackend(
    checkoutInstance: CheckoutHandler,
): CreateContractSyncSDK['offers'] {
    return checkoutInstance.basket.offers.map((offer) => ({
        ...offer,
        contracts: offer.contracts.map((contract: Contract) => {
            let paymentValuesToUse:
                | Contract['contractAttributes']['cc']
                | Contract['contractAttributes']['ach']
                | undefined = undefined

            if (checkoutInstance.paymentOption === 'ach') {
                paymentValuesToUse = contract.contractAttributes.ach
                    .discountProfileAttributes
                    ? {
                          ...contract.contractAttributes.ach,
                          discountProfileAttributes: {
                              achDiscountCodeApplied:
                                  contract.contractAttributes.ach
                                      .discountProfileAttributes
                                      ?.achDiscountCodeApplied,
                              netAmountBeforeDiscount:
                                  contract.contractAttributes.ach
                                      .discountProfileAttributes
                                      ?.netAmountBeforeDiscount,
                          } as Contract['contractAttributes']['ach']['discountProfileAttributes'],
                      }
                    : contract.contractAttributes.ach
            } else if (checkoutInstance.paymentOption === 'card') {
                paymentValuesToUse = contract.contractAttributes.cc
            }

            if (!paymentValuesToUse) {
                throw new Error('no payment values to use')
            }

            return {
                contractsAttributes: {
                    effectiveDate: contract.contractAttributes.effectiveDate,
                    levelOfCoverCode:
                        contract.contractAttributes.levelOfCoverCode,
                    productVariantId:
                        contract.contractAttributes.productVariantId,
                    periodOfCover: contract.contractAttributes.periodOfCover,
                    thirdPartyContractExpiryDate:
                        contract.contractAttributes
                            .thirdPartyContractExpiryDate,
                    ...paymentValuesToUse,
                },
                itemDetails: {
                    itemId: contract.itemDetails.itemId,
                },
            } satisfies CreateContractSyncSDK['offers'][0]['contracts'][number]
        }),
    }))
}
